"use client"

import { useState, useMemo } from "react"
import {
  getPasswordRequirementStatus,
  PASSWORD_REQUIREMENT_UI,
  PASSWORD_EASY_PATTERNS_MESSAGE,
} from "../../../lib/utils/passwordUtils"
import PasswordEyeIcon from "../../../lib/utils/PasswordEyeIcon"
import "./PasswordField.css"

const CHECK_ICON = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3334 4L6.00002 11.3333L2.66669 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Common password input with inline requirements (signup-style design).
 * Use for Create Password / New Password everywhere: signup, invite, reset password, profile.
 * - Four requirements with checkmarks that turn green when met
 * - Optional inline error for rule 4 only when it adds detail (name / same-as-current); generic pattern copy is on the checklist
 */
export function PasswordFieldWithRequirements({
  id,
  name = "password",
  label = "Create Password",
  value,
  onChange,
  placeholder = "Enter Password",
  error = "",
  fullName = "",
  currentPwd = "",
  required = false,
  className = "",
  inputClassName = "",
  autoComplete = "new-password",
}) {
  const [showPassword, setShowPassword] = useState(false)

  const requirements = useMemo(
    () => getPasswordRequirementStatus(value, fullName, currentPwd),
    [value, fullName, currentPwd],
  )

  const rule4Failed = Boolean(value) && !requirements.avoidsEasyPatterns

  /** Checklist row 4 already describes generic pattern failures — avoid duplicate red text. */
  const showPatternErrorText =
    Boolean(error) &&
    rule4Failed &&
    error !== PASSWORD_EASY_PATTERNS_MESSAGE

  return (
    <div className={`pw-field ${className}`}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required && <span className="pw-required">*</span>}
        </label>
      )}
      <div className="pw-input-wrapper">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-required={required}
          className={`pw-input ${inputClassName} ${rule4Failed ? "pw-input-error" : ""}`}
        />
        <button
          type="button"
          className="pw-toggle-btn"
          onClick={() => setShowPassword((p) => !p)}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          <PasswordEyeIcon isVisible={showPassword} />
        </button>
      </div>
      <div className="pw-requirements">
        {PASSWORD_REQUIREMENT_UI.map(({ key, label }) => (
          <div key={key} className={`pw-req ${requirements[key] ? "met" : ""}`}>
            {CHECK_ICON}
            <span>{label}</span>
          </div>
        ))}
      </div>
      {showPatternErrorText && <p className="pw-error">{error}</p>}
    </div>
  )
}

/**
 * Simple password input (no requirements list). Use for Confirm Password.
 */
export function PasswordFieldSimple({
  id,
  name = "confirmPassword",
  label = "Confirm Password",
  value,
  onChange,
  placeholder = "Re-enter Password",
  error = "",
  required = false,
  className = "",
  inputClassName = "",
  autoComplete = "new-password",
}) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className={`pw-field pw-field-simple ${className}`}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required && <span className="pw-required">*</span>}
        </label>
      )}
      <div className="pw-input-wrapper">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-required={required}
          className={`pw-input ${inputClassName} ${error ? "pw-input-error" : ""}`}
        />
        <button
          type="button"
          className="pw-toggle-btn"
          onClick={() => setShowPassword((p) => !p)}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          <PasswordEyeIcon isVisible={showPassword} />
        </button>
      </div>
      {error && <p className="pw-error">{error}</p>}
    </div>
  )
}

export default PasswordFieldWithRequirements
